import React, { FC } from 'react';
import classNames from 'classnames/bind';
import Text from '../Text';
import styles from './index.pcss';

const cx = classNames.bind(styles);

export type TierType = 'SILVER' | 'GOLD' | 'PLATINUM';

export interface TierBadgeProps {
  tier: string;
  label?: string;
  /** Flag-configured badge artwork (centralLoyaltyConfig.icons.<tier><Light|Dark>Badge). */
  badge?: string;
  customClass?: string;
  variant?: 'default' | 'gradient';
}

/**
 * Loyalty tier badge (SILVER / GOLD / PLATINUM).
 * Renders the flag-configured badge image when one is available and falls back
 * to the CSS pill otherwise; only that fallback styling is keyed off the tier.
 */
const TierBadge: FC<TierBadgeProps> = (props: TierBadgeProps) => {
  const { tier, label = '', badge = '', customClass = '', variant = 'default' } = props;

  if (!tier) return null;

  const tierKey = String(tier).toUpperCase();
  const badgeLabel = label || tierKey;

  if (badge) {
    return (
      <img
        className={cx('tier-badge__image', customClass)}
        src={badge}
        alt={`${badgeLabel} tier`}
      />
    );
  }

  // For gradient variant, text color is handled by CSS; for default, use text color
  const textColor = variant === 'gradient' ? 'Nero' : (tierKey === 'PLATINUM' ? 'White' : 'Nero');

  return (
    <span
      className={cx(
        'tier-badge',
        `tier-badge--${tierKey.toLowerCase()}`,
        `tier-badge--${variant}`,
        customClass
      )}
      aria-label={`${badgeLabel} tier`}
    >
      <Text variant="T1" color={textColor} weight="bold" textTransform="is-uppercase">
        {badgeLabel}
      </Text>
    </span>
  );
};

export default TierBadge;
